/** One row of "Agent inputs": a field, where its value comes from, and that value. */

import { Select } from "@/components/ui/select";
import { Segmented } from "@/components/ui/tabs";
import type { InputField } from "@/features/agents/types";
import { CredentialSelect } from "../credentials-section";
import { fixedSource, fixedText } from "./input-mapping";

export type Source = "dataset" | "fixed" | "credential";

const SOURCES: { key: Source; label: string }[] = [
  { key: "dataset", label: "Dataset" },
  { key: "fixed", label: "Fixed" },
  { key: "credential", label: "Credential" },
];

export function InputRow({
  input,
  fields,
  source,
  value,
  credentialId,
  onSource,
  onChange,
  onCredential,
}: {
  input: InputField;
  fields: string[];
  source: Source;
  value: string;
  credentialId: string;
  onSource: (source: Source) => void;
  onChange: (source: string) => void;
  onCredential: (id: string) => void;
}) {
  const label = input.name;
  return (
    <div className="grid grid-cols-1 items-center gap-x-3 gap-y-1.5 px-3 py-1.5 text-xs sm:grid-cols-[minmax(0,200px)_auto_minmax(0,1fr)]">
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-mono font-semibold" title={label}>
          {label}
        </span>
        {input.description && <span className="text-faint">{input.description}</span>}
      </span>
      <Segmented label={`Source for ${label}`} value={source} onChange={onSource} items={SOURCES} />
      {source === "dataset" && (
        <ColumnSelect label={label} fields={fields} value={value} onChange={onChange} />
      )}
      {source === "fixed" && (
        <FixedValue input={input} label={label} value={fixedText(value)} onChange={onChange} />
      )}
      {source === "credential" && (
        <CredentialSelect
          forKey={input.key}
          label={`Credential for ${label}`}
          value={credentialId}
          onChange={onCredential}
        />
      )}
    </div>
  );
}

/** A dataset column; none means the field is not sent. */
function ColumnSelect({
  label,
  fields,
  value,
  onChange,
}: {
  label: string;
  fields: string[];
  value: string;
  onChange: (source: string) => void;
}) {
  const known = value === "" || fields.some((f) => `input.${f}` === value);
  return (
    <Select
      label={`Column for ${label}`}
      buttonClassName="h-8 text-xs"
      value={value}
      onChange={onChange}
      options={[
        { value: "", label: "No column (not sent)" },
        ...(known ? [] : [{ value, label: value }]),
        ...fields.map((f) => ({ value: `input.${f}`, label: f })),
      ]}
    />
  );
}

/** The one value for the whole experiment: one of the platform's options, else text. */
function FixedValue({
  input,
  label,
  value,
  onChange,
}: {
  input: InputField;
  label: string;
  value: string;
  onChange: (source: string) => void;
}) {
  const set = (v: string) => onChange(fixedSource(v));
  if (input.options.length)
    return (
      <Select
        label={`Fixed value for ${label}`}
        buttonClassName="h-8 text-xs"
        value={value}
        onChange={set}
        options={[
          ...(value && !input.options.some((o) => o.value === value) ? [{ value, label: value }] : []),
          ...input.options.map((o) => ({ value: o.value, label: o.label })),
        ]}
      />
    );
  return (
    <input
      aria-label={`Fixed value for ${label}`}
      className="inp h-8 w-full text-xs"
      value={value}
      placeholder="Same for every item"
      onChange={(e) => set(e.target.value)}
    />
  );
}
